import { useEffect, useState } from "react";
import { getUtmBreakdown } from "services/marketing-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";
import UtmBreakdownPieChart from "./UtmBreakdownPieChart";
import UtmBreakdownTable from "./UtmBreakdownTable";

function BreakdownWithChart({
  tableTitle,
  columnLabel,
  chartTitle,
  rows,
  emptyMessage,
  shared,
}) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
      <UtmBreakdownTable
        title={tableTitle}
        columnLabel={columnLabel}
        rows={rows}
        emptyMessage={emptyMessage}
        className="mb-0 h-full"
        {...shared}
      />
      <UtmBreakdownPieChart
        title={chartTitle}
        rows={rows}
        emptyMessage={emptyMessage}
        className="mb-0 h-full"
        {...shared}
      />
    </div>
  );
}

export default function UtmBreakdownTables({ api, filters, compact = false }) {
  const [bySource, setBySource] = useState([]);
  const [byMedium, setByMedium] = useState([]);
  const [byCampaign, setByCampaign] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setBySource([]);
      setByMedium([]);
      setByCampaign([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getUtmBreakdown(api, { filters });
        if (!cancelled) {
          setBySource(result?.bySource ?? []);
          setByMedium(result?.byMedium ?? []);
          setByCampaign(result?.byCampaign ?? []);
        }
      } catch {
        if (!cancelled) {
          setBySource([]);
          setByMedium([]);
          setByCampaign([]);
          setError("Failed to load UTM breakdown. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const shared = { isLoading, error, filters };
  const sourceEmpty = "No UTM source data for the selected period.";

  if (compact) {
    return (
      <UtmBreakdownTable
        title="UTM Source Breakdown"
        columnLabel="Source"
        rows={bySource}
        emptyMessage={sourceEmpty}
        {...shared}
      />
    );
  }

  return (
    <>
      <BreakdownWithChart
        tableTitle="UTM Source Breakdown"
        columnLabel="Source"
        chartTitle="Contact Share by Source"
        rows={bySource}
        emptyMessage={sourceEmpty}
        shared={shared}
      />
      <BreakdownWithChart
        tableTitle="UTM Medium Breakdown"
        columnLabel="Medium"
        chartTitle="Contact Share by Medium"
        rows={byMedium}
        emptyMessage="No UTM medium data for the selected period."
        shared={shared}
      />
      <BreakdownWithChart
        tableTitle="UTM Campaign Breakdown"
        columnLabel="Campaign"
        chartTitle="Contact Share by Campaign"
        rows={byCampaign}
        emptyMessage="No UTM campaign data for the selected period."
        shared={shared}
      />
    </>
  );
}
